<script setup lang="ts">
const { t: $t } = useI18n()

import { isAfter } from 'date-fns'
import type { ApiCustomerModuleMachineryPartnerSalesReservationsGetAll } from '~/layers/customer-module/types'

const props = defineProps<{
  machineryId: string
}>()

const { reservation } = useCustomerModuleQuery()
const { data } = reservation.allByMachineryId(props.machineryId)
const confirmDeleteReservationId = shallowRef<string>()
const showPartnerSaleReservationCommentsId = shallowRef<string>()

const columns: OneDataColumn<ApiCustomerModuleMachineryPartnerSalesReservationsGetAll>[] = [
  {
    title: $t('customerModule.reservation.createdAt.label'),
    key: 'createdAt',
    render: ({ createdAt }) => useDateAsString(createdAt),
  },
  {
    title: $t('customerModule.reservation.createdBy.label'),
    key: 'createdBy',
    render: ({ createdBy, createdByEmail }) => createdBy.name ?? createdByEmail,
  },
  {
    title: $t('customerModule.reservation.expiresAt.label'),
    key: 'expiresAt',
    render: ({ expiresAt }) => useDateAsString(expiresAt),
  },
  {
    title: $t('customerModule.reservation.status.label'),
    key: 'id',
    render: ({ expiresAt, deletedAt }) => {
      if (deletedAt) {
        return $t('customerModule.reservation.status.deleted')
      }

      if (expiresAt && isAfter(new Date(), new Date(expiresAt))) {
        return $t('customerModule.reservation.status.expired')
      }

      return $t('customerModule.reservation.status.active')
    },
  },
]

const notification = useNotification()
const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const { mutate: mutateDeleteReservation } = useMutation({
  mutationFn: $trpc.customerModule.machinery.partnerSales.deleteReservation.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.reservation.notification.delete.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'reservation', 'allByMachineryId', props.machineryId] }),
      queryClient.invalidateQueries({ queryKey: ['machines', props.machineryId, 'public'] }),
    ])

    notification.success({ title: $t('customerModule.reservation.notification.delete.success'), duration: 4500 })
  },
})

function actionButtons(row: ApiCustomerModuleMachineryPartnerSalesReservationsGetAll): CustomerModuleAction[] {
  if ((row.expiresAt && isAfter(new Date(), new Date(row.expiresAt))) || row.deletedAt) {
    return ['showComments']
  }

  return ['showComments', 'delete']
}

function getContainerClass(row: ApiCustomerModuleMachineryPartnerSalesReservationsGetAll) {
  if (row.deletedAt) {
    return 'isDeleted'
  }

  if (row.expiresAt && isAfter(new Date(), new Date(row.expiresAt))) {
    return 'isExpired'
  }

  return 'isActive'
}

function deleteReservation() {
  if (confirmDeleteReservationId.value) {
    mutateDeleteReservation({ id: confirmDeleteReservationId.value })
  }
}
</script>

<template>
  <CustomerModuleDataCard :title="$t('customerModule.machinery.details.reservation.title')">
    <CustomerModuleMachineryDetailsPartnerSalesReservationCommentsPopup
      v-if="showPartnerSaleReservationCommentsId"
      :partner-sale-reservation-id="showPartnerSaleReservationCommentsId"
      @close="showPartnerSaleReservationCommentsId = undefined"
    />

    <TheConfirmPopup
      v-if="confirmDeleteReservationId"
      @confirm="deleteReservation"
      @close="confirmDeleteReservationId = undefined"
    >
      {{ $t('customerModule.reservation.confirm.description') }}
    </TheConfirmPopup>

    <div class="listContainer">
      <CustomerModuleList
        v-if="data"
        :data="data"
        :action-buttons
        :columns
        :get-container-class
        @show-comments="({ id }) => showPartnerSaleReservationCommentsId = id"
        @delete="({ id }) => confirmDeleteReservationId = id"
      />
    </div>
  </CustomerModuleDataCard>
</template>

<style scoped>
.listContainer :deep(.isDeleted) {
  @apply bg-red-100
}

.listContainer :deep(.isActive) {
  @apply bg-green-100
}

.listContainer :deep(.isExpired) {
  @apply bg-gray-100
}
</style>
